"use client";
import React, { useState, useRef, useEffect } from "react";
import "./puja-campaign-camera.scss";
import Image from "next/image";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Webcam from "react-webcam";

import { convertImageFormat } from "@/utils/convertImageFormat";
import { resizeImageDimensions } from "@/utils/resizeImageDimensions";
import { reduceImageSize } from "@/utils/reduceImageSize";
import { convertToJpegBase64 } from "@/utils/base64jpeg";

import Btn from "../btn";

import cameraFrame from "@/../public/campaign/puja-2025/camera-frame.png";

export default function Camera({ onNextComp, setCapturedImg, credits }) {
  const webRef = useRef(null);
  const uploadPictureRef = useRef(null);

  const [img, setImg] = useState({ img: "", type: "camera" });
  const [heic2any, setHeic2any] = useState(null);

  useEffect(() => {
    import("heic2any").then((mod) => setHeic2any(() => mod.default));
  }, []);

  async function getUserSelfie(id) {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open("PujaCampaignUserData", 1);

      request.onsuccess = function (event) {
        const db = event.target.result;
        const tx = db.transaction("userSelfie", "readonly");
        const store = tx.objectStore("userSelfie");
        const getReq = store.get(id);

        getReq.onsuccess = () => resolve(getReq.result?.img || null);
        getReq.onerror = reject;
      };

      request.onerror = reject;
    });
  }

  const saveUserPhoto = async (id, val) => {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open("PujaCampaignUserData", 1);

      request.onupgradeneeded = function (event) {
        const db = event.target.result;
        if (db.objectStoreNames.contains("userSelfie")) {
          db.deleteObjectStore("userSelfie");
        }
        db.createObjectStore("userSelfie", { keyPath: "id" });
      };

      request.onsuccess = function (event) {
        const db = event.target.result;
        const tx = db.transaction("userSelfie", "readwrite");
        tx.objectStore("userSelfie").put({ id, img: val });

        tx.oncomplete = () => resolve("Saved/Updated!");
        tx.onerror = reject;
      };

      request.onerror = reject;
    });
  };

  const handleHeicFileUpload = async (file) => {
    try {
      if (!heic2any) {
        toast.error("Unable to process HEIC image.");
        return null;
      }

      const convertedBlob = await heic2any({
        blob: file,
        toType: "image/jpeg",
      });

      const jpegFile = new File(
        [convertedBlob],
        file.name.replace(/\.heic$/i, ".jpeg"),
        { type: "image/jpeg" }
      );

      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onloadend = async (event) => {
          const tempImg = new window.Image();
          tempImg.src = event.target.result;

          tempImg.onload = async () => {
            try {
              const converted = await convertImageFormat(tempImg, jpegFile);
              const resized = await resizeImageDimensions(converted);
              const final = await reduceImageSize(resized, jpegFile.size);

              const finalReader = new FileReader();
              finalReader.onloadend = () => resolve(finalReader.result);
              finalReader.readAsDataURL(final);
            } catch (err) {
              reject(err);
            }
          };
        };

        reader.onerror = reject;
        reader.readAsDataURL(jpegFile);
      });
    } catch (error) {
      console.error("HEIC conversion error:", error);
      toast.error("Failed to upload HEIC file.");
      return null;
    }
  };

  const handleUploadPicture = () => {
    uploadPictureRef.current.click();
  };

  const handleUploadPictureChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    let type = file.type;

    if (!type || type === "") {
      if (file.name.toLowerCase().endsWith(".heic")) {
        type = "image/heic";
      }
    }

    try {
      let jpegBase64 = null;

      if (type === "image/heic") {
        jpegBase64 = await handleHeicFileUpload(file);
      } else if (type.startsWith("image")) {
        jpegBase64 = await convertToJpegBase64(file);
      } else {
        toast.warning("Please upload a valid image.");
        return;
      }

      if (jpegBase64) {
        setImg({ img: jpegBase64, type: "upload" });
        await saveUserPhoto(1, jpegBase64);
      }
    } catch (error) {
      console.error(error);
      toast.error("Error processing image.");
    }
  };

  const handleCapture = async () => {
    const screenshot = webRef.current.getScreenshot();
    if (!screenshot) return;

    setImg({ img: screenshot, type: "camera" });
    await saveUserPhoto(1, screenshot);
  };

  const handleRetake = () => {
    setImg({ img: "", type: "camera" });
  };

  const handleSubmit = () => {
    if (!img.img) {
      toast.error("Please capture or upload your image");
      return;
    }
    setCapturedImg(img);
    !credits ? onNextComp("payment") : onNextComp("output");
  };

  return (
    <div className="flex-col-center PujaCampaignCamera">
      <h1 className="pageHeading">CAPTURE YOUR PHOTO</h1>

      {img.type === "camera" && (
        <div className="flex-row-center webcamContainer">
          <Image src={cameraFrame} alt="camera" className="cameraFrame" />

          {!img.img ? (
            <Webcam
              ref={webRef}
              id="webcam"
              videoConstraints={{
                width: 720,
                height: 720,
                aspectRatio: 1,
                facingMode: "user",
              }}
              forceScreenshotSourceSize={true}
              screenshotQuality={1}
              mirrored={true}
              screenshotFormat="image/jpeg"
            />
          ) : (
            <Image
              src={img.img}
              alt="captured"
              className="caturedImg"
              width={720}
              height={720}
            />
          )}
        </div>
      )}

      {img.img && img.type === "upload" && (
        <div className="flex-row-center uploadedImgContainer">
          <Image
            src={img.img}
            alt="uploaded"
            className="uploadImg"
            width={720}
            height={720}
          />
          <Image
            src={cameraFrame}
            alt="camera"
            className="cameraFrame"
            width={720}
            height={720}
          />
        </div>
      )}

      <footer className="flex-col-center footerPujaCampaign">
        <div className="flex-row-center topBtns">
          {!img.img ? (
            <div className="flex-col-center btnContainer">
              <span onClick={handleCapture}>
                <Btn btnTxt="CAPTURE" />
              </span>
              <span onClick={handleUploadPicture}>
                <Btn btnTxt="UPLOAD" />
              </span>
            </div>
          ) : (
            <div className="flex-col-center btnContainer">
              <span onClick={handleSubmit}>
                <Btn btnTxt="PROCEED" />
              </span>
              <span onClick={handleRetake}>
                <Btn btnTxt="RETAKE" />
              </span>
            </div>
          )}

          <input
            type="file"
            ref={uploadPictureRef}
            onChange={handleUploadPictureChange}
            style={{ display: "none" }}
            accept="image/*"
          />
        </div>
      </footer>
    </div>
  );
}